<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :show-close="false"
    :append-to-body="true"
    width="520px"
    custom-class="login-announcement-dialog"
    @closed="handleClosed"
  >
    <div class="announcement-card">
      <button
        class="announcement-card__close"
        type="button"
        :aria-label="$t('header.announcementDialog.closeAria')"
        @click="closeDialog"
      >
        ×
      </button>

      <header class="announcement-card__header">
        <div class="announcement-card__meta">
          <div class="announcement-card__badge">
            <i class="el-icon-s-opportunity announcement-card__badge-icon"></i>
            <span>{{ $t("header.announcementDialog.badge") }}</span>
          </div>
          <span
            v-if="displayVersion"
            class="announcement-card__version"
          >
            {{ displayVersion }}
          </span>
        </div>
        <h2 class="announcement-card__title">
          {{ displayTitle }}
        </h2>
        <p
          v-if="formattedPublishTime"
          class="announcement-card__time"
        >
          {{ $t("header.announcementDialog.publishedAt", { time: formattedPublishTime }) }}
        </p>
      </header>

      <div class="announcement-card__body">
        <div class="announcement-card__content" v-html="displayContent">
        </div>
      </div>

      <footer class="announcement-card__footer">
        <button
          class="announcement-card__confirm"
          type="button"
          @click="closeDialog"
        >
          {{ $t("header.announcementDialog.confirm") }}
        </button>
      </footer>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "LoginAnnouncementDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 登录接口返回的单条公告：{ id, title, content, publishTime, projectVersion }
    announcement: {
      type: Object,
      default: null,
    },
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    displayTitle() {
      const title =
        this.announcement && String(this.announcement.title || "").trim();
      return title || this.$t("header.announcementDialog.fallbackTitle");
    },
    /** 展示后端 projectVersion，缺省时不渲染版本标签 */
    displayVersion() {
      const version =
        this.announcement && String(this.announcement.projectVersion || "").trim();
      return version || "";
    },
    displayContent() {
      if (!this.announcement) {
        return "";
      }
      return String(this.announcement.content || "");
    },
    /** 将 publishTime 格式化为本地可读时间 */
    formattedPublishTime() {
      const raw =
        this.announcement && this.announcement.publishTime
          ? String(this.announcement.publishTime).trim()
          : "";
      if (!raw) {
        return "";
      }
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) {
        return raw;
      }
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate(),
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
  },
  methods: {
    /** 关闭弹窗并通知父级清理暂存公告 */
    closeDialog() {
      this.dialogVisible = false;
      this.$emit("confirm");
    },
    handleClosed() {
      this.$emit("closed");
    },
  },
};
</script>

<style lang="less">
/* append-to-body 后需非 scoped 才能盖掉 el-dialog 默认白边 */
.login-announcement-dialog.el-dialog {
  margin-top: 18vh !important;
  background: transparent !important;
  border: none !important;
  border-radius: 14px;
  box-shadow: none !important;
  outline: none !important;
  overflow: visible;

  .el-dialog__header {
    display: none;
    padding: 0;
    border: none;
  }

  .el-dialog__body {
    padding: 0;
    background: transparent;
    border: none;
    color: #f8fafc;
  }
}
</style>

<style lang="less" scoped>
.announcement-card {
  position: relative;
  border: none;
  border-radius: 14px;
  overflow: hidden;
  background:
    radial-gradient(
      ellipse 90% 60% at 50% -10%,
      rgba(138, 63, 244, 0.28),
      transparent 55%
    ),
    linear-gradient(180deg, #1a1b28 0%, #12131b 100%);
  box-shadow:
    0 0 0 1px rgba(138, 63, 244, 0.22),
    0 24px 64px rgba(0, 0, 0, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.announcement-card__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.4);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
}

.announcement-card__header {
  padding: 28px 28px 0;
}

.announcement-card__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 40px 14px 0;
}

.announcement-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(138, 63, 244, 0.16);
  color: #d4bfff;
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.04em;
}

.announcement-card__badge-icon {
  font-size: 13px;
  color: #c4b5fd;
}

.announcement-card__version {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(124, 58, 237, 0.18);
  color: #e9d5ff;
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.02em;
}

.announcement-card__title {
  margin: 0 40px 10px 0;
  color: #f7f4ff;
  font-size: 22px;
  font-weight: 750;
  line-height: 1.35;
  word-break: break-word;
}

.announcement-card__time {
  margin: 0;
  color: rgba(232, 232, 244, 0.42);
  font-size: 12px;
  line-height: 1.4;
}

.announcement-card__body {
  padding: 18px 28px 8px;
  max-height: 420px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 6px;
  }
}

.announcement-card__content {
  padding: 16px 18px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
  color: rgba(245, 243, 255, 0.88);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
}

.announcement-card__footer {
  padding: 20px 28px 28px;
}

.announcement-card__confirm {
  display: block;

  border: 0;
  border-radius: 16px;
  background: linear-gradient(90deg, #8a3ff4 0%, #7437ee 100%);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(118, 55, 238, 0.26);
  transition: background 0.2s ease;
	margin: 0 auto;
	padding: 6px 20px;

  &:hover,
  &:focus {
    background: linear-gradient(90deg, #9652f7 0%, #7f44f1 100%);
  }
}

@media (max-width: 768px) {
  .announcement-card__header {
    padding: 24px 20px 0;
  }

  .announcement-card__body {
    padding: 16px 20px 4px;
  }

  .announcement-card__footer {
    padding: 16px 20px 20px;
  }

  .announcement-card__title {
    font-size: 18px;
  }
}
</style>
